/**
 * Agent Management Component
 * Admin interface for managing MCP-based agents
 */

import { useState } from 'react';
import { Plus, Search, RefreshCw, AlertCircle, Upload } from 'lucide-react';
import { useAgent } from '@/context/AgentContext';
import { AgentCard } from './AgentCard';
import { InstallAgentModal } from './InstallAgentModal';
import { ExportAgentModal } from './ExportAgentModal';
import { ImportAgentWizard } from './ImportAgentWizard';
import { agentService } from '@/services/agentService';
import { Button, LoadingSpinner } from '@/components/Common';
import type { Agent, AgentInstallRequest } from '@/types/agent.types';

export function AgentManagement() {
  const {
    agents,
    selectedAgent,
    isLoading,
    error,
    installAgent,
    updateAgent,
    deleteAgent,
    duplicateAgent,
    activateAgent,
    deactivateAgent,
    setDefaultAgent,
    selectAgent,
    refreshAgents,
    importAgent,
  } = useAgent();

  const [showInstallModal, setShowInstallModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [exportTargetAgent, setExportTargetAgent] = useState<Agent | null>(null);
  const [showImport, setShowImport] = useState(false);

  const handleExport = (agentId: string) => {
    const target = agents.find((a) => a.id === agentId) ?? null;
    setExportTargetAgent(target);
  };

  const handleInstall = async (request: AgentInstallRequest) => {
    try {
      // Use the config the user entered in the wizard. If the caller did not
      // supply one (legacy paths / tests), fall back to a framework-shaped
      // empty placeholder so the agent can be edited after install.
      let config = request.config;
      if (!config) {
        switch (request.framework) {
          case 'azure-ai-agent':
            config = {
              framework: 'azure-ai-agent',
              config: { endpoint: '', apiKey: '', deploymentId: '' },
            };
            break;
          case 'copilot':
            config = {
              framework: 'copilot',
              config: { endpoint: '', apiKey: '' },
            };
            break;
          case 'mcp':
            config = {
              framework: 'mcp',
              config: { command: '' },
            };
            break;
        }
      }

      await installAgent({
        ...request,
        config,
      });
      setShowInstallModal(false);
    } catch (err) {
      console.error('Failed to install agent:', err);
    }
  };

  const handleDuplicate = async (agentId: string, name?: string) => {
    try {
      const copy = await duplicateAgent(agentId, name);
      // Open the new agent in the configuration editor by selecting it,
      // so the admin can finish customizing the clone right away.
      selectAgent(copy.id);
    } catch (err) {
      console.error('Failed to duplicate agent:', err);
    }
  };

  const handleUpdateConfiguration = async (
    agentId: string,
    updates: {
      name?: string;
      description?: string;
      config?: Agent['config'];
      systemPrompt?: string;
      suggestedPrompts?: Agent['suggestedPrompts'];
      icon?: string;
      welcomeMessage?: Agent['welcomeMessage'];
      webSearchConfig?: Agent['webSearchConfig'];
    }
  ) => {
    try {
      await updateAgent(agentId, updates);
    } catch (err) {
      console.error('Failed to update agent configuration:', err);
      throw err;
    }
  };

  const filteredAgents = agents
    .filter((agent) => {
      // Filter by status
      if (filterStatus === 'active' && agent.status !== 'active') return false;
      if (filterStatus === 'inactive' && agent.status === 'active') return false;

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          agent.name.toLowerCase().includes(query) ||
          agent.description.toLowerCase().includes(query) ||
          agent.framework.toLowerCase().includes(query)
        );
      }

      return true;
    });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" label="Loading agents..." />
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden bg-bg-secondary">
      {/* Header */}
      <div className="border-b border-border-default bg-bg-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              Agent Management
            </h1>
            <p className="mt-1 text-sm text-text-secondary">
              Install and manage AI agents for your chatbot
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={refreshAgents}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Import Agent
            </Button>
            <Button
              onClick={() => setShowInstallModal(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Install Agent
            </Button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents..."
              className="w-full rounded-lg border border-border-default py-2 pl-10 pr-4 focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={
                `rounded-lg px-4 py-2 text-sm font-medium ` +
                `transition-colors ` +
                `focus-visible:outline-none focus-visible:ring-2 ` +
                `focus-visible:ring-stellar-primary ` +
                `focus-visible:ring-offset-2 ${
                  filterStatus === 'all'
                    ? 'bg-stellar-primary text-white'
                    : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary'
                }`
              }
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={
                `rounded-lg px-4 py-2 text-sm font-medium ` +
                `transition-colors ` +
                `focus-visible:outline-none focus-visible:ring-2 ` +
                `focus-visible:ring-stellar-primary ` +
                `focus-visible:ring-offset-2 ${
                  filterStatus === 'active'
                    ? 'bg-stellar-primary text-white'
                    : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary'
                }`
              }
            >
              Active
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={
                `rounded-lg px-4 py-2 text-sm font-medium ` +
                `transition-colors ` +
                `focus-visible:outline-none focus-visible:ring-2 ` +
                `focus-visible:ring-stellar-primary ` +
                `focus-visible:ring-offset-2 ${
                  filterStatus === 'inactive'
                    ? 'bg-stellar-primary text-white'
                    : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary'
                }`
              }
            >
              Inactive
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg border border-error bg-error-light p-4 text-sm text-error">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Stats */}
      <div className="border-b border-border-default bg-bg-primary px-6 py-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="rounded-lg border border-border-default bg-bg-secondary p-4">
            <div className="text-2xl font-bold text-text-primary">
              {agents.length}
            </div>
            <div className="text-sm text-text-secondary">Total Agents</div>
          </div>
          <div className="rounded-lg border border-border-default bg-success-light p-4">
            <div className="text-2xl font-bold text-success">
              {agents.filter((a) => a.status === 'active').length}
            </div>
            <div className="text-sm text-success">Active</div>
          </div>
          <div className="rounded-lg border border-border-default bg-bg-secondary p-4">
            <div className="text-2xl font-bold text-text-primary">
              {agents.filter((a) => a.status === 'inactive').length}
            </div>
            <div className="text-sm text-text-secondary">Inactive</div>
          </div>
          <div className="rounded-lg border border-border-default bg-bg-tertiary p-4">
            <div className="text-2xl font-bold text-text-primary">
              {new Set(agents.map((a) => a.framework)).size}
            </div>
            <div className="text-sm text-stellar-primary-dark">Frameworks</div>
          </div>
        </div>
      </div>

      {/* Agent List */}
      <div className="h-full overflow-y-auto px-6 py-6">
        {filteredAgents.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <div className="text-4xl mb-4">🤖</div>
            <h3 className="text-lg font-medium text-text-primary">
              {searchQuery || filterStatus !== 'all'
                ? 'No agents found'
                : 'No agents installed'}
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by installing your first agent'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Button
                onClick={() => setShowInstallModal(true)}
                className="mt-4"
              >
                Install Agent
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                isSelected={selectedAgent?.id === agent.id}
                onSelect={(agent) => selectAgent(agent.id)}
                onActivate={activateAgent}
                onDeactivate={deactivateAgent}
                onDelete={deleteAgent}
                onDuplicate={handleDuplicate}
                onSetDefault={setDefaultAgent}
                onUpdateConfiguration={handleUpdateConfiguration}
                onExport={handleExport}
              />
            ))}
          </div>
        )}
      </div>

      {/* Install Modal */}
      <InstallAgentModal
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        onInstall={handleInstall}
      />

      {/* Export Modal */}
      <ExportAgentModal
        isOpen={exportTargetAgent !== null}
        agent={exportTargetAgent}
        onClose={() => setExportTargetAgent(null)}
        onExport={({ includeSecrets }) =>
          agentService.exportAgent(exportTargetAgent!.id, { includeSecrets })
        }
      />

      {/* Import Wizard */}
      <ImportAgentWizard
        isOpen={showImport}
        existingAgents={agents}
        onClose={() => setShowImport(false)}
        onImport={importAgent}
      />
    </div>
  );
}
